import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { spacingVars } from '@astryxdesign/core/theme/tokens.stylex';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { VStack } from '@astryxdesign/core/VStack';
import { MoreMenu } from '@astryxdesign/core/MoreMenu';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Icon } from '@astryxdesign/core/Icon';
import { FileSpreadsheet, FileText, LayoutDashboard } from 'lucide-react';
import { Button } from '@astryxdesign/core/Button';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from '@astryxdesign/core/Toast';
import { useMediaQuery } from '@astryxdesign/core/hooks';
import { actionPermissions, canUseAcquiringDocuments, sectionPermissions } from '@qronstat/shared';
import { reportHref } from '../reports/station/search';
import { documentsHref } from '../acquiring/search';
import { useApp } from '../app/context';
import { mobileQuery } from '../app/layout';
import { useSavedMutations } from '../api/saved';
import { PeriodPicker } from '../components/filters/PeriodPicker';
import { FilterPanel } from '../components/filters/FilterPanel';
import { FilterChips } from '../components/filters/FilterChips';
import { countFilters } from '../state/search';
import { cubesOf } from '../components/filters/model';
import { useScreen } from './ScreenContext';
export const filtersEvent = 'qs:open-filters';
export interface CustomizeControl {
  active: boolean;
  onToggle: () => void;
}
export function ScreenHeader({
  title,
  customize
}: {
  title: string;
  customize?: CustomizeControl;
}) {
  const intl = useIntl();
  const toast = useToast();
  const {
    can,
    meta,
    user
  } = useApp();
  const isMobile = useMediaQuery(mobileQuery);
  const {
    screen,
    state,
    search,
    update,
    setFilters,
    setRealOnly
  } = useScreen();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const saved = useSavedMutations();
  const navigate = useNavigate();
  const quick = screen.quickFilters;
  const report = screen.report && can(sectionPermissions.stations) ? screen.report : null;
  const documents = screen.documents && canUseAcquiringDocuments(user.permissions) ? screen.documents : null;
  const hasPeriod = quick.includes('period') || screen.id === 'summary';
  useEffect(() => {
    const open = () => setFiltersOpen(true);
    window.addEventListener(filtersEvent, open);
    return () => window.removeEventListener(filtersEvent, open);
  }, []);
  const count = countFilters(state);
  const scope = useMemo(() => cubesOf(meta, screen.widgets), [meta, screen.widgets]);
  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        body: intl.formatMessage({
          id: 'screen.linkCopied'
        })
      });
    } catch {
      toast({
        body: intl.formatMessage({
          id: 'screen.linkCopyFailed'
        }),
        type: 'error'
      });
    }
  };
  const saveView = () => {
    const name = `${title} · ${new Date().toLocaleDateString()}`;
    saved.create.mutate({
      kind: 'view',
      screen: screen.id,
      name,
      definition: {
        path: window.location.pathname,
        search
      }
    }, {
      onSuccess: () => toast({
        body: intl.formatMessage({
          id: 'screen.viewSaved'
        })
      }),
      onError: () => toast({
        body: intl.formatMessage({
          id: 'screen.viewSaveFailed'
        }),
        type: 'error'
      })
    });
  };
  return <div className="x1pzlopt x1oa1p4a xeuugli">
      <VStack gap={2}>
        <HStack gap={2} vAlign="center" hAlign="between" wrap="wrap">
          <div className="xeuugli xs83m0k">
            <Heading level={isMobile ? 2 : 1}>{title}</Heading>
          </div>
          <HStack gap={1} vAlign="center">
            {hasPeriod ? <PeriodPicker value={{
            period: state.period,
            compare: state.compare,
            compareRange: state.compareRange,
            timeMode: state.timeMode
          }} showTimeMode={quick.includes('timeMode')} onChange={next => {
            const patch: Record<string, unknown> = {};
            if (next.period) patch.period = next.period;
            if (next.compare) {
              patch.cmp = next.compare === 'none' ? undefined : next.compare;
              patch.cmpr = next.compare === 'custom' ? next.compareRange ?? undefined : undefined;
            }
            if (next.timeMode) patch.tz = next.timeMode;
            update(patch);
          }} /> : null}
            <FilterPanel screenId={screen.id} scope={scope} quickKeys={quick} value={{
            filters: state.filters,
            realOnly: state.realOnly
          }} count={count} isOpen={filtersOpen} onOpenChange={setFiltersOpen} onApply={v => update({
            f: v.filters.length ? v.filters : undefined,
            all: v.realOnly ? undefined : true
          })} />
            {report ? isMobile ? <IconButton label={intl.formatMessage({
            id: 'card.report'
          })} icon={<Icon icon={FileText} />} variant="ghost" size="sm" onClick={() => void navigate(reportHref(report, {
            period: state.period
          }) as never)} /> : <Button label={intl.formatMessage({
            id: 'card.report'
          })} icon={<Icon icon={FileText} />} variant="ghost" size="sm" onClick={() => void navigate(reportHref(report, {
            period: state.period
          }) as never)} /> : null}
            {documents ? isMobile ? <IconButton label={intl.formatMessage({
            id: 'card.documents'
          })} icon={<Icon icon={FileSpreadsheet} />} variant="ghost" size="sm" onClick={() => void navigate(documentsHref(documents) as never)} /> : <Button label={intl.formatMessage({
            id: 'card.documents'
          })} icon={<Icon icon={FileSpreadsheet} />} variant="ghost" size="sm" onClick={() => void navigate(documentsHref(documents) as never)} /> : null}
            {customize ? <IconButton label={intl.formatMessage({
            id: customize.active ? 'layout.done' : 'layout.customize'
          })} icon={<Icon icon={LayoutDashboard} />} variant={customize.active ? 'primary' : 'ghost'} size="sm" aria-pressed={customize.active} onClick={customize.onToggle} /> : null}
            <MoreMenu label={intl.formatMessage({
            id: 'screen.more'
          })} size="sm" presentation="adaptive" alignment="end" items={[{
            id: 'share',
            label: intl.formatMessage({
              id: 'screen.share'
            }),
            onClick: () => void share()
          }, ...(can(actionPermissions.reportsManage) ? [{
            id: 'save',
            label: intl.formatMessage({
              id: 'screen.saveView'
            }),
            onClick: saveView
          }] : [])]} />
          </HStack>
        </HStack>
        <FilterChips filters={state.filters} realOnly={state.realOnly} onRemove={i => setFilters(state.filters.filter((_, j) => j !== i))} onClearRealOnly={() => setRealOnly(true)} onOpen={() => setFiltersOpen(true)} />
      </VStack>
    </div>;
}